import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { api } from '../lib/api';
import { supabase } from '../lib/supabaseClient';
import type { User } from '../../../shared/contracts';
import type { Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  onboardingComplete: boolean;
  login: (role: string) => Promise<void>;
  loginWithSupabase: (session: Session) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_KEY = 'session';
const USER_KEY = 'user';
const AUTH_TOKEN_KEY = 'auth_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if onboarding is complete (user has created or joined a company)
  const onboardingComplete = user ? user.onboardingStatus === 'completed' : false;

  // Polling reference for lock status
  const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    // Don't check auth if user just logged out (indicated by localStorage flag)
    const justLoggedOut = sessionStorage.getItem('justLoggedOut');
    if (justLoggedOut) {
      console.log('[Auth] User just logged out, skipping auth check');
      sessionStorage.removeItem('justLoggedOut');
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  // Setup lock status polling for ALL users (including locked)
  useEffect(() => {
    if (!user) {
      // Clear interval if user logs out
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Define polling function with no dependencies
    const pollLockStatus = async () => {
      try {
        const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!authToken) return;

        const response = await api.get<{ user: User }>('/auth/me', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        // Update user with fresh data from API (handles both lock and unlock)
        console.log('[Auth] Lock status check - user:', response.user.email, 'isLocked:', response.user.isLocked);

        // Only update if there's an actual change to prevent unnecessary re-renders
        setUser(prevUser => {
          if (!prevUser) return response.user;

          // Check if anything actually changed
          const hasChanged =
            prevUser.isLocked !== response.user.isLocked ||
            prevUser.role !== response.user.role ||
            prevUser.email !== response.user.email ||
            prevUser.name !== response.user.name;

          if (hasChanged) {
            console.log('[Auth] User data changed, updating state');
            localStorage.setItem(USER_KEY, JSON.stringify(response.user));
            return response.user;
          }

          // No changes, return same reference
          return prevUser;
        });
      } catch (error: any) {
        // Handle 403 locked account error
        if (error?.status === 403 && error?.message?.toLowerCase().includes('lock')) {
          console.warn('[Auth] User is locked (403)');
          // Get fresh user data from localStorage
          const storedUser = localStorage.getItem(USER_KEY);
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUser({ ...userData, isLocked: true });
            localStorage.setItem(USER_KEY, JSON.stringify({ ...userData, isLocked: true }));
          }
        }
      }
    };

    // Check immediately on mount
    console.log('[Auth] Initial lock status check for user:', user.email);
    pollLockStatus();

    // Check only when user navigates to a new route (via page visibility change)
    // This reduces polling from every 3 seconds to only when user returns to tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Auth] Page became visible, checking lock status');
        pollLockStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Optional: Check every 60 seconds as fallback (instead of 3 seconds)
    // This is a much longer interval for safety
    pollIntervalRef.current = setInterval(pollLockStatus, 60000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [user?.id]); // Only depend on user id, not the whole user object

  // Listen for lock status changes from API client
  useEffect(() => {
    const handleUserLocked = (event: Event) => {
      const customEvent = event as CustomEvent;
      const lockedUser = customEvent.detail;
      console.log('[Auth] Received userLocked event, updating user state:', lockedUser.email);
      setUser(prevUser => {
        if (!prevUser || prevUser.isLocked !== lockedUser.isLocked) {
          return lockedUser;
        }
        return prevUser;
      });
    };

    const handleUserUnlocked = (event: Event) => {
      const customEvent = event as CustomEvent;
      const unlockedUser = customEvent.detail;
      console.log('[Auth] Received userUnlocked event, updating user state:', unlockedUser.email, 'isLocked:', unlockedUser.isLocked);
      setUser(prevUser => {
        if (!prevUser || prevUser.isLocked !== unlockedUser.isLocked) {
          return unlockedUser;
        }
        return prevUser;
      });
    };

    // Also listen for unlock events when user data is updated externally
    const handleStorageChange = () => {
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          console.log('[Auth] Detected storage change, updating user state:', user.email, 'isLocked:', user.isLocked);
          setUser(prevUser => {
            if (!prevUser || prevUser.isLocked !== user.isLocked) {
              return user;
            }
            return prevUser;
          });
        } catch (e) {
          console.error('[Auth] Failed to parse stored user from storage change:', e);
        }
      }
    };

    window.addEventListener('userLocked', handleUserLocked);
    window.addEventListener('userUnlocked', handleUserUnlocked);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('userLocked', handleUserLocked);
      window.removeEventListener('userUnlocked', handleUserUnlocked);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const checkAuth = async () => {
    try {
      // Migrate old keys to new ones if they exist
      const oldUser = localStorage.getItem('demo_user');
      const oldSession = localStorage.getItem('demo_session');
      if (oldUser && !localStorage.getItem(USER_KEY)) {
        localStorage.setItem(USER_KEY, oldUser);
      }
      if (oldSession && !localStorage.getItem(SESSION_KEY)) {
        localStorage.setItem(SESSION_KEY, oldSession);
      }
      // Clean up old keys
      localStorage.removeItem('demo_user');
      localStorage.removeItem('demo_session');

      // First check localStorage for demo session
      const storedUser = localStorage.getItem(USER_KEY);
      const storedSession = localStorage.getItem(SESSION_KEY);

      if (storedUser && storedSession) {
        // Validate session is not expired
        const session = JSON.parse(storedSession);
        if (new Date(session.expiresAt) > new Date()) {
          const user = JSON.parse(storedUser);
          console.log('[Auth] Found stored user:', user.email, 'isLocked:', user.isLocked);

          // If stored user doesn't have isLocked field, fetch fresh data from backend
          if (user.isLocked === undefined) {
            console.log('[Auth] Stored user missing isLocked field, fetching fresh data');
            try {
              const authToken = session.token;
              const response = await api.get<{ user: User }>('/auth/me', {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              });

              console.log('[Auth] Fresh user from backend:', response.user.email, 'isLocked:', response.user.isLocked);

              // Update stored user with fresh data
              localStorage.setItem(USER_KEY, JSON.stringify(response.user));

              // Check if user is locked
              if (response.user.isLocked) {
                console.log('[Auth] User is locked (from fresh fetch)');
                setUser(response.user);
                setLoading(false);
                return;
              }

              setUser(response.user);
              setLoading(false);
              return;
            } catch (err: any) {
              console.error('[Auth] Error fetching fresh user data:', err);

              // Check if error includes user data with isLocked=true
              if (err.data?.user?.isLocked === true) {
                console.log('[Auth] Found locked user in error response');
                setUser(err.data.user);
                setLoading(false);
                return;
              }

              // Fall through to normal flow
            }
          }

          // Check if user is locked - if so, don't set them as logged in
          if (user.isLocked) {
            console.log('[Auth] User is locked, not setting as logged in');
            setUser(user); // Keep user data for locked account screen
            setLoading(false);
            return;
          }
          setUser(user);
          setLoading(false);
          return;
        } else {
          // Session expired, clear storage
          localStorage.removeItem(USER_KEY);
          localStorage.removeItem(SESSION_KEY);
        }
      }

      // Check for Supabase session
      const { data: { session: supabaseSession } } = await supabase.auth.getSession();

      if (supabaseSession) {
        // We have a Supabase session, get user from our backend
        const authToken = supabaseSession.access_token;
        localStorage.setItem(AUTH_TOKEN_KEY, authToken);

        try {
          const response = await api.get<{ user: User }>('/auth/me', {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });

          // Check if user is locked
          if (response.user.isLocked) {
            console.log('[Auth] User is locked from Supabase session:', response.user.email);
            setUser(response.user);
            setLoading(false);
            return;
          }

          // expires_at is in seconds, convert to milliseconds for Date
          const expiresAt = supabaseSession.expires_at
            ? new Date(supabaseSession.expires_at * 1000).toISOString()
            : new Date(Date.now() + 3600000).toISOString(); // 1 hour default

          setUser(response.user);
          localStorage.setItem(USER_KEY, JSON.stringify(response.user));
          localStorage.setItem(SESSION_KEY, JSON.stringify({
            token: authToken,
            expiresAt,
          }));
          setLoading(false);
          return;
        } catch (err: any) {
          console.error('[Auth] Failed to get user from backend:', err);

          // Check if error is because user is locked
          if (err.status === 403 && err.data?.user?.isLocked === true) {
            console.log('[Auth] User is locked (403 from Supabase session):', err.data.user.email);
            setUser(err.data.user);
            setLoading(false);
            return;
          }

          // User doesn't exist in our database or session is invalid, sign out from Supabase
          await supabase.auth.signOut();
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          localStorage.removeItem(SESSION_KEY);
        }
      }

      setUser(null);
    } catch (error) {
      console.log('No active session');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (role: string) => {
    try {
      const response = await api.post<{ success: boolean; user: User; session: { token: string; expiresAt: string } }>('/demo-auth/login', {
        role,
      });

      // Check if user is locked
      if (response.user.isLocked) {
        console.warn('[Auth] User is locked after demo login:', response.user.email);
        // Store session and user data anyway for locked account page
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        localStorage.setItem(SESSION_KEY, JSON.stringify(response.session));
        localStorage.setItem(AUTH_TOKEN_KEY, response.session.token);
        setUser(response.user);
        throw new Error('Account locked');
      }

      // Store session in localStorage for persistence
      localStorage.setItem(USER_KEY, JSON.stringify(response.user));
      localStorage.setItem(SESSION_KEY, JSON.stringify(response.session));
      localStorage.setItem(AUTH_TOKEN_KEY, response.session.token);

      setUser(response.user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const loginWithSupabase = useCallback(async (session: Session, onLocked?: () => void) => {
    try {
      const authToken = session.access_token;
      localStorage.setItem(AUTH_TOKEN_KEY, authToken);

      // Get or create user from our backend
      try {
        const response = await api.get<{ user: User }>('/auth/me', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        // Check if user is locked
        if (response.user.isLocked) {
          console.warn('[Auth] User account is locked');
          // Store user info for locked account screen
          const expiresAt = session.expires_at
            ? new Date(session.expires_at * 1000).toISOString()
            : new Date(Date.now() + 3600000).toISOString();

          localStorage.setItem(USER_KEY, JSON.stringify(response.user));
          localStorage.setItem(SESSION_KEY, JSON.stringify({
            token: authToken,
            expiresAt,
          }));

          setUser(response.user);
          if (onLocked) onLocked();
          throw new Error('Account locked');
        }

        // Store session in localStorage for persistence
        // expires_at is in seconds, convert to milliseconds for Date
        const expiresAt = session.expires_at
          ? new Date(session.expires_at * 1000).toISOString()
          : new Date(Date.now() + 3600000).toISOString(); // 1 hour default

        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        localStorage.setItem(SESSION_KEY, JSON.stringify({
          token: authToken,
          expiresAt,
        }));

        setUser(response.user);
      } catch (err: any) {
        // Check if error is because account is locked
        if (err.data?.user?.isLocked === true) {
          console.warn('[Auth] User account is locked (from error response)');
          const expiresAt = session.expires_at
            ? new Date(session.expires_at * 1000).toISOString()
            : new Date(Date.now() + 3600000).toISOString();

          // Store locked user data
          localStorage.setItem(USER_KEY, JSON.stringify(err.data.user));
          localStorage.setItem(SESSION_KEY, JSON.stringify({
            token: authToken,
            expiresAt,
          }));

          setUser(err.data.user);
          if (onLocked) onLocked();
          throw new Error('Account locked');
        }

        // Re-throw other errors
        throw err;
      }
    } catch (error) {
      console.error('Supabase login failed:', error);
      throw error;
    }
  }, []);

  const logout = async () => {
    try {
      console.log('[Auth] Logging out user...');

      // Set flag to prevent AuthContext from checking auth on next mount
      sessionStorage.setItem('justLoggedOut', 'true');

      // Sign out from Supabase first (with global scope to clear all sessions)
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        console.error('[Auth] Supabase signOut error:', error);
      } else {
        console.log('[Auth] Signed out from Supabase');
      }

      // Clear all localStorage keys related to auth
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(AUTH_TOKEN_KEY);

      // Clear Supabase's session storage keys
      localStorage.removeItem('sb-euoltrkqyvpxtaegvxyz-auth-token');
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-auth-token');

      // Clear all localStorage keys that start with 'supabase' or 'sb-'
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase') || key.startsWith('sb-')) {
          console.log(`[Auth] Removing localStorage key: ${key}`);
          localStorage.removeItem(key);
        }
      });

      console.log('[Auth] Cleared local storage');

      // Also set Supabase session to null
      await supabase.auth.setSession(null);
      console.log('[Auth] Set Supabase session to null');

      setUser(null);
      console.log('[Auth] Logout complete');
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      // Still clear the user and storage even if logout fails
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(AUTH_TOKEN_KEY);

      // Clear Supabase auth storage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase') || key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });

      try {
        await supabase.auth.setSession(null);
      } catch (e) {
        console.error('[Auth] Failed to clear Supabase session:', e);
      }

      setUser(null);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(prevUser => {
      if (!prevUser) {
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
        return updatedUser;
      }

      // Check if anything actually changed
      const hasChanged =
        prevUser.id !== updatedUser.id ||
        prevUser.isLocked !== updatedUser.isLocked ||
        prevUser.role !== updatedUser.role ||
        prevUser.email !== updatedUser.email ||
        prevUser.name !== updatedUser.name;

      if (hasChanged) {
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
        return updatedUser;
      }

      // No changes, return same reference
      return prevUser;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, onboardingComplete, login, loginWithSupabase, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
